import * as React from 'react'

import { inject, observer } from 'mobx-react'

import MobileResourceEditDrawer from '~/client/src/mobile/components/MobileResource/MobileResourceEditDrawer'
import { useMobileResourceEditRecord } from '~/client/src/mobile/components/MobileResource/useMobileResourceEditRecord'
import {
  LinkedResourceEdit,
  useLinkedResourceEditor,
} from '~/client/src/shared/components/ResourceLink'
import { ResourceEdit } from '~/client/src/shared/components/ResourceViewEdit'
import { ResourceEditDetailsBody } from '~/client/src/shared/components/ResourceViewEdit/ResourceEditDetailsBody'
import { buildResourceTagSections } from '~/client/src/shared/components/ResourceViewEdit/resourceEditSectionBuilders'
import { useSensorEquipmentLinkField } from '~/client/src/shared/hooks/useEquipmentSensorLinkField'
import useSensors from '~/client/src/shared/hooks/useSensors'
import InitialState from '~/client/src/shared/stores/InitialState'

import {
  useMobileResourceActions,
  useMobileResourceMeta,
} from '../../components/MobileResource/context'

interface IProps {
  state?: InitialState
}

const MobileStruxTagsEditPanel: React.FC<IProps> = ({ state }) => {
  const { closeEdit } = useMobileResourceActions()
  const { resourceTypeId, runtimeContext } = useMobileResourceMeta()
  const {
    isOpen,
    activeRecordId,
    selectedRecord,
    editableRecord,
    clearActiveRecord,
  } = useMobileResourceEditRecord()

  const projectId = state?.activeProject?.id
  const { save, schema } = useSensors({ projectId })

  const linkField = useSensorEquipmentLinkField({
    projectId,
    sourceRecordId: activeRecordId === 'new' ? null : activeRecordId,
    sourceRecord: selectedRecord,
  })

  const { openedLinkedRecordId, openLinkedRecord, closeLinkedRecord } =
    useLinkedResourceEditor(linkField.linkRootProps.linkedItem)

  const qrCodeUrl = React.useMemo(() => {
    if (!activeRecordId) return null
    return `${window.location.origin}/resource/${resourceTypeId}/${activeRecordId}`
  }, [activeRecordId, resourceTypeId])

  const handleSave = React.useCallback(
    async (formData: Record<string, any>) => {
      await save(activeRecordId, formData)
    },
    [save, activeRecordId],
  )

  if (!activeRecordId) return null

  return (
    <>
      <MobileResourceEditDrawer
        open={isOpen}
        onClose={closeEdit}
        onCloseComplete={clearActiveRecord}
      >
        <ResourceEdit.Root
          recordId={activeRecordId}
          record={editableRecord}
          resourceTypeId={resourceTypeId}
          runtimeContext={runtimeContext}
          onClose={closeEdit}
          onSave={handleSave}
          qrCodeUrl={qrCodeUrl}
          resourceSchema={schema}
        >
          <ResourceEditDetailsBody
            isCreationMode={activeRecordId === 'new'}
            linkField={linkField}
            onOpenLinked={openLinkedRecord}
            sections={buildResourceTagSections({
              projectId,
              infuseId: selectedRecord?.infuseId,
            })}
          />
        </ResourceEdit.Root>
      </MobileResourceEditDrawer>
      <LinkedResourceEdit
        open={!!openedLinkedRecordId}
        recordId={openedLinkedRecordId}
        projectId={projectId}
        resourceTypeId={linkField.targetResourceTypeId}
        onClose={closeLinkedRecord}
        shell={children => (
          <MobileResourceEditDrawer
            open={!!openedLinkedRecordId}
            onClose={closeLinkedRecord}
          >
            {children}
          </MobileResourceEditDrawer>
        )}
      />
    </>
  )
}

export default inject('state')(observer(MobileStruxTagsEditPanel))
